/**
 * Lógica PURA del editor de cotizaciones: los pasos del asistente y la réplica del cálculo de
 * alcance del servidor. Sin React, para que el hook y los pasos lean exactamente lo mismo.
 */

/** Los cuatro pasos del asistente, en orden. `n` es lo que viaja en la URL (`?paso=2`). */
export const PASOS = [
  { n: 1, clave: 'cliente', titulo: 'Cliente y país', descripcion: 'Quién es y dónde factura' },
  { n: 2, clave: 'paquete', titulo: 'Paquete', descripcion: 'Edición, módulos y asientos' },
  { n: 3, clave: 'cobro', titulo: 'Cobro y servicios', descripcion: 'Cargos, descuento y arquitectura' },
  { n: 4, clave: 'revisar', titulo: 'Revisar y enviar', descripcion: 'Guardar, demo y envío al dueño' },
]

export const MODALIDADES = [
  { value: 'local', label: 'Local / SQLite (self-host)' },
  { value: 'vps', label: 'VPS propio' },
  { value: 'nube', label: 'Nube gestionada' },
]

// Áreas que trae la BASE de toda edición y que NINGÚN módulo vende: nunca se apagan por el
// paquete (apagar `pos` dejaría al negocio sin caja). Espeja `cd2bCatalogo.AREAS_BASE`.
export const AREAS_BASE = ['nucleo', 'pos']

/** Cierra el grafo de dependencias del catálogo (misma regla que la autoridad del servidor). */
function cerrarDependencias(base, areaMeta) {
  const out = new Set(base)
  let cambio = true
  while (cambio) {
    cambio = false
    for (const a of [...out]) {
      for (const req of areaMeta?.[a]?.requiere || []) {
        if (!out.has(req)) { out.add(req); cambio = true }
      }
    }
  }
  return out
}

/**
 * Réplica EXACTA del cálculo del servidor (`resolverAreasActivas`, sin recorte por paquete) para
 * previsualizar lo que verá el cliente. Si diverge, el asesor vendería una cosa y el cliente
 * vería otra — por eso el catálogo (áreas, dependencias, módulos) viene del backend, no está
 * hardcodeado aquí.
 */
export function resolverAlcance({ edicion, modulosSel, catalogo }) {
  const ed = (catalogo.ediciones || []).find((e) => e.edicion === edicion)
  const areasEdicion = ed?.areas || []
  const modulos = catalogo.modulos || []
  // Ya no hay recorte por paquete (lo pagado no se recorta): la edición entrega todas sus áreas
  // y los módulos marcados SUMAN las suyas. Mismo criterio que el servidor.
  const vendidas = modulosSel.flatMap((code) => modulos.find((m) => m.code === code)?.areas || [])

  const base = new Set([...areasEdicion, ...vendidas])
  base.add('nucleo')
  const cerrado = cerrarDependencias(base, catalogo.areaMeta)
  const orden = catalogo.areas || []
  return {
    edicionDef: ed,
    areasEdicion,
    activas: orden.filter((a) => cerrado.has(a)),
    apagadas: orden.filter((a) => areasEdicion.includes(a) && !cerrado.has(a)),
    // Sin recorte no hay áreas «activas solo por dependencia»: se conserva vacío por compatibilidad.
    porDependencia: [],
  }
}

/** ¿La edición cubre TODAS las áreas del módulo? (si no, el módulo no es vendible en ella). */
export const edicionCubre = (areasEdicion, modulo) => (modulo.areas || []).every((a) => areasEdicion.includes(a))

/** Días que faltan para una fecha (negativo si ya pasó). */
export const diasParaVencer = (fecha) =>
  Math.ceil((new Date(fecha).getTime() - Date.now()) / (24 * 60 * 60 * 1000))

/**
 * El impuesto del país tal como lo publica `/public/cd2b/paises` («IVA 13%», «ISV 15%»). La API
 * lo deriva de la tabla de tasas vigente (docs/172), así que aquí no hay ninguna tasa escrita: si
 * el texto no trae un porcentaje se devuelve sin `tasa` y el resumen no inventa un total.
 */
export function impuestoDelPais(texto) {
  const t = String(texto || '').trim()
  if (!t) return null
  const m = t.match(/^(.*?)\s*(\d+(?:[.,]\d+)?)\s*%/)
  if (!m) return { nombre: t, tasa: null, texto: t }
  return { nombre: m[1].trim() || 'Impuesto', tasa: Number(m[2].replace(',', '.')) / 100, texto: t }
}
